import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, masterPassword, investorPassword) => 
    api.post('/auth/reset-password', { 
      token, 
      master_password: masterPassword, 
      investor_password: investorPassword 
    }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token/${token}`),
};

// Account APIs
export const accountAPI = {
  switchAccount: (accountType) => api.post('/account/switch', { account_type: accountType }),
  submitKYC: (data) => api.post('/account/kyc', data),
  getMetrics: () => api.get('/account/metrics'),
  getKYCStatus: () => api.get('/account/kyc/status'),
  uploadKYCDocument: (documentType, file) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('file', file);
    return api.post('/account/kyc/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Admin APIs
export const adminAPI = {
  getPendingDeposits: () => api.get('/admin/pending-deposits'),
  getPendingWithdrawals: () => api.get('/admin/pending-withdrawals'),
  approveDeposit: (transactionId) => api.post(`/admin/approve-deposit/${transactionId}`),
  rejectDeposit: (transactionId) => api.post(`/admin/reject-deposit/${transactionId}`),
  approveWithdrawal: (transactionId) => api.post(`/admin/approve-withdrawal/${transactionId}`),
  rejectWithdrawal: (transactionId) => api.post(`/admin/reject-withdrawal/${transactionId}`),
  getAllUsers: () => api.get('/admin/all-users'),
  getPendingKYC: () => api.get('/admin/pending-kyc'),
  approveKYC: (userId) => api.post(`/admin/approve-kyc/${userId}`),
  rejectKYC: (userId) => api.post(`/admin/reject-kyc/${userId}`),
  getMarketStatus: () => api.get('/admin/market-status'),
  setMarketStatus: (isOpen, overrideEnabled = true) => api.post('/admin/market-status', { is_open: isOpen, override_enabled: overrideEnabled }),
  // Account switching
  switchAccount: (userId) => api.post(`/admin/switch-account/${userId}`),
  getUserTrades: (userId) => api.get(`/admin/user-trades/${userId}`),
  getUserSummary: (userId) => api.get(`/admin/user-summary/${userId}`),
  // User management
  getUser: (userId) => api.get(`/admin/user/${userId}`),
  modifyUser: (data) => api.post('/admin/modify-user', data),
  deleteUser: (userId) => api.delete(`/admin/delete-user/${userId}`),
  resetUserPassword: (userId, masterPassword, investorPassword) => 
    api.post('/admin/reset-user-password', { user_id: userId, master_password: masterPassword, investor_password: investorPassword }),
  updateUserBalance: (userId, balance, demoBalance) => 
    api.post('/admin/update-user-balance', { user_id: userId, balance, demo_balance: demoBalance }),
  // Balance recalculation
  recalculateAllBalances: () => api.post('/admin/recalculate-balances'),
  recalculateUserBalance: (userId) => api.post(`/admin/recalculate-user-balance/${userId}`),
};

// Market APIs
export const marketAPI = {
  getPairs: (category, search) => {
    const params = new URLSearchParams();
    if (category && category !== 'all') params.append('category', category);
    if (search) params.append('search', search);
    return api.get(`/market/pairs?${params.toString()}`);
  },
  getCategories: () => api.get('/market/categories'),
  getPrice: (pair) => api.get(`/market/price/${pair.replace('/', '-')}`),
  getChart: (pair, periods = 50, timeframe = 'H1') => 
    api.get(`/market/chart/${pair.replace('/', '-')}?periods=${periods}&timeframe=${timeframe}`),
  getNews: () => api.get('/market/news'),
  getStatus: () => api.get('/market/status'),
};

// Trading APIs
export const tradingAPI = {
  openTrade: (data) => api.post('/trade/open', data),
  closeTrade: (tradeId) => api.post(`/trade/close/${tradeId}`),
  getOpenTrades: () => api.get('/trade/open'),
  getTradeHistory: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.period) searchParams.append('period', params.period);
    if (params.startDate) searchParams.append('start_date', params.startDate);
    if (params.endDate) searchParams.append('end_date', params.endDate);
    const queryString = searchParams.toString();
    return api.get(`/trade/history${queryString ? `?${queryString}` : ''}`);
  },
};

// Payment APIs
export const paymentAPI = {
  getWalletAddress: () => api.get('/payments/wallet-address'),
  createDeposit: (amount, txHash) => api.post('/payments/deposit', { amount, tx_hash: txHash }),
  createDepositWithProof: (amount, txHash, proofFile) => {
    const formData = new FormData();
    formData.append('amount', amount);
    if (txHash) formData.append('tx_hash', txHash);
    formData.append('proof', proofFile);
    return api.post('/payments/deposit/with-proof', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  createWithdrawal: (amount, walletAddress) => api.post('/payments/withdraw', { amount, wallet_address: walletAddress }),
  getTransactions: () => api.get('/payments/transactions'),
};

export default api;
